import React from "react";
import { getTimeAgo } from "utils/dateUtils";
import { useFlightStatus } from "hooks/useFlightStatus";
import LiveFlightStatus from "./LiveFlightStatus";

interface AnsweredFlightDetailsProps {
  /** The load-request row (typed loosely to match the parent list). */
  flight: any;
  /**
   * True when the row's status is "Updated Answer": the same fields hold the
   * latest LoadResponse values, so the body only changes its labelling to make
   * the refresh visible.
   */
  isUpdatedAnswer?: boolean;
}

/**
 * The qualifiers the responder set while answering the open request, which the
 * seat counts on their own cannot express: a negative count means the class is
 * overbooked, and a `more*` flag means there are seats but the responder could
 * not see how many. Without these the row reads "0 First" for both "no seats"
 * and "seats exist, count unknown", which are opposite answers.
 *
 * Each entry pairs the class's seat count with its own more-seats flag. Both
 * field names come from the LoadResponse row as the list endpoint returns it.
 */
const SEAT_CLASSES: { label: string; countKey: string; moreKey: string }[] = [
  { label: "First", countKey: "firstClass", moreKey: "moreFirstClass" },
  { label: "Business", countKey: "busClass", moreKey: "moreBusClass" },
  { label: "Eco+", countKey: "ecoPlus", moreKey: "moreEcoPlus" },
  { label: "Economy", countKey: "eco", moreKey: "moreEco" },
];

/**
 * Builds the "Additional Info" chips for one answered row.
 *
 * Deliberately conservative: a chip appears only when the underlying field is
 * actually present and set. A count of 0 is NOT treated as "no such class" —
 * 0 and "class does not exist" are stored identically today, so inferring the
 * second from the first would put a wrong claim on screen.
 */
const additionalInfoFor = (flight: any): string[] => {
  const chips: string[] = [];
  if (!flight) return chips;

  // Set by the "I don't know how many non-rev passengers are listed" toggle on
  // the reply form. Read under both spellings because the answer row and the
  // reply payload have not always agreed on this one.
  if (flight.unknownNonRevCount === true || flight.isNonRevCountUnknown === true) {
    chips.push("Non-Rev Count Unavailable");
  }

  SEAT_CLASSES.forEach(({ label, countKey, moreKey }) => {
    const count = flight[countKey];
    if (typeof count === "number" && count < 0) {
      // Named per class: several classes can be overbooked at once, and an
      // unqualified "Overbooked" would not say which.
      chips.push(`${label} Class Overbooked`);
    }
    if (flight[moreKey]) {
      // Shortened from "… Seats Available - Count Unavailable" so the chip
      // fits on one line beside the seat counts.
      chips.push(`${label} Seats - Count Unavailable`);
    }
  });

  return chips;
};

// Drive the single flight-status badge from the live flightState (falling back
// to "Scheduled" until the backend publishes one). Keeps the established green
// "Scheduled" look while reflecting real states like Landed / Cancelled.
const badgeFor = (state?: string): { text: string; className: string } => {
  switch ((state || "").toLowerCase()) {
    case "active":
    case "en-route":
    case "enroute":
      return { text: state as string, className: "bg-blue-500" };
    case "landed":
      return { text: state as string, className: "bg-gray-500" };
    case "delayed":
    case "diverted":
      return { text: state as string, className: "bg-orange-500" };
    case "cancelled":
    case "canceled":
    case "incident":
      return { text: state as string, className: "bg-red-500" };
    case "scheduled":
    case "":
    case undefined as any:
      return { text: "Scheduled", className: "bg-green-500" };
    default:
      return { text: state as string, className: "bg-green-500" };
  }
};

/**
 * Body of the "Answered" accordion: responder + open seats + live flight status.
 * Owns the Firestore listener for the row (via useFlightStatus) so the header
 * badge and the live panel below share a single subscription.
 */
const AnsweredFlightDetails: React.FC<AnsweredFlightDetailsProps> = ({
  flight,
  isUpdatedAnswer = false,
}) => {
  const enabled = flight?.isEnabledStatus === true;
  const { status } = useFlightStatus(flight?.id, enabled);

  const badge = badgeFor(status?.flightState);
  const additionalInfo = additionalInfoFor(flight);

  return (
    <div className="bg-white rounded-b-lg mt-3">
      <div className="p-4 border-t border-gray-300">
        {/* Flex rather than a 12-column grid: a fixed span reserved the same
            width for "Answered By" whatever the responder's name was, leaving a
            dead gap before the seat counts. Here the two outer columns hug their
            content and the middle one takes what is left. */}
        <div className="flex flex-col md:flex-row md:items-start gap-3 sm:gap-4 lg:gap-5">
          <div className="flex flex-col gap-1 md:shrink-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-lightgray-200 text-xs">
                {isUpdatedAnswer ? "Updated By" : "Answered By"}
              </p>
            </div>
            <p className="text-grease-100 text-sm font-medium">
              {flight?.responderName || "Unknown"}
            </p>
            <div className="flex items-center gap-1 text-xs text-lightgray-150">
              <i className="st-time"></i>
              {/* When the request was ANSWERED — use answeredDate (the freshest
                  LoadResponse timestamp), NOT the request's createdDate/
                  modifiedDate which stay frozen at request-creation time and so
                  don't reflect re-answers. Falls back to modifiedDate only if
                  answeredDate is absent. */}
              <p>
                {getTimeAgo(flight?.answeredDate ?? flight?.modifiedDate)}
              </p>
            </div>
          </div>
          {/* items-start from md up: Additional Info can run to two rows of
              chips, and bottom-aligning would drop the other two headings out
              of line with it. Three columns only fit side by side from xl —
              below that they wrap rather than overflow, which is why nowrap is
              not applied at lg. */}
          {/* With no Additional Info column the two seat groups would sit hard
              against the responder, leaving all the slack on the right — centre
              them instead. With chips present the row is full, so they stay
              left-aligned and the chips take the remaining width. */}
          <div
            className={`flex md:flex-nowrap flex-wrap items-end md:items-start md:flex-1 md:min-w-0 gap-3 sm:gap-4 ${
              additionalInfo.length === 0 ? "lg:justify-center" : ""
            }`}
          >
            <div className="flex flex-col gap-2 w-full md:w-auto md:shrink-0 md:ml-3 lg:ml-5">
              <p className="text-lightgray-200 text-xs">Open Seats</p>
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <div className="flex flex-col items-center border border-lightgray-100 py-0.5 flex-1 min-w-[42px] sm:w-10 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-[13px] font-medium">
                    {flight?.firstClass || 0}
                  </p>
                  <p className="text-lightgray-150 text-[10px]">First</p>
                </div>
                <div className="flex flex-col items-center border border-lightgray-100 py-0.5 flex-1 min-w-[42px] sm:w-10 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-[13px] font-medium">
                    {flight?.busClass || 0}
                  </p>
                  <p className="text-lightgray-150 text-[10px]">Bus</p>
                </div>
                <div className="flex flex-col items-center border border-lightgray-100 py-0.5 flex-1 min-w-[42px] sm:w-10 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-[13px] font-medium">
                    {flight?.ecoPlus || 0}
                  </p>
                  <p className="text-lightgray-150 text-[10px]">Eco+</p>
                </div>
                <div className="flex flex-col items-center border border-lightgray-100 py-0.5 flex-1 min-w-[42px] sm:w-10 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-[13px] font-medium">
                    {flight?.eco || 0}
                  </p>
                  <p className="text-lightgray-150 text-[10px]">Eco</p>
                </div>
                <div className="flex flex-col items-center bg-lightgray-100 bg-opacity-40 py-0.5 flex-1 min-w-[42px] sm:w-10 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-[13px] font-medium">
                    {flight?.total || 0}
                  </p>
                  <p className="text-lightgray-150 text-[10px]">Total</p>
                </div>
              </div>
            </div>
            <div className="w-px h-10 bg-lightgray-100 hidden md:block"></div>
            <div className="flex flex-col gap-2 w-full md:w-auto md:shrink-0">
              <p className="text-lightgray-200 text-xs">
                Listed Non-Rev Passengers
              </p>
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <div className="flex flex-col items-center border border-lightgray-100 py-0.5 flex-1 min-w-[42px] sm:w-10 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-[13px] font-medium">
                    {flight?.nonFirstClass || 0}
                  </p>
                  <p className="text-lightgray-150 text-[10px]">First</p>
                </div>
                <div className="flex flex-col items-center border border-lightgray-100 py-0.5 flex-1 min-w-[42px] sm:w-10 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-[13px] font-medium">
                    {flight?.nonBusClass || 0}
                  </p>
                  <p className="text-lightgray-150 text-[10px]">Bus</p>
                </div>
                <div className="flex flex-col items-center border border-lightgray-100 py-0.5 flex-1 min-w-[42px] sm:w-10 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-[13px] font-medium">
                    {flight?.nonEcoPlus || 0}
                  </p>
                  <p className="text-lightgray-150 text-[10px]">Eco+</p>
                </div>
                <div className="flex flex-col items-center border border-lightgray-100 py-0.5 flex-1 min-w-[42px] sm:w-10 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-[13px] font-medium">
                    {flight?.nonEco || 0}
                  </p>
                  <p className="text-lightgray-150 text-[10px]">Eco</p>
                </div>
                <div className="flex flex-col items-center bg-lightgray-100 bg-opacity-40 py-0.5 flex-1 min-w-[42px] sm:w-10 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-[13px] font-medium">
                    {flight?.nonRevListed || 0}
                  </p>
                  <p className="text-lightgray-150 text-[10px]">Total</p>
                </div>
              </div>
            </div>

            {/* Additional Info — the qualifiers the responder saved while
                answering, sitting alongside the counts they modify rather than
                below them. Omitted entirely (divider included) when the answer
                carried none, so an ordinary answer gains no empty column. */}
            {additionalInfo.length > 0 && (
              <>
                <div className="w-px h-10 bg-lightgray-100 hidden md:block"></div>
                {/* The only column allowed to shrink. The seat groups are
                    shrink-0, so when the row runs out of width the pressure
                    lands here and the chips below wrap onto more lines instead
                    of this whole column dropping under them. */}
                <div className="flex flex-col gap-2 w-full md:w-auto md:min-w-0">
                  <p className="text-lightgray-200 text-xs">Additional Info</p>
                  {/* One row of chips, each kept on a single line by
                      whitespace-nowrap. flex-wrap is the safety valve for an
                      answer that carries an unusual number of them. */}
                  <div className="flex flex-row flex-wrap items-center gap-1.5">
                    {additionalInfo.map((info) => (
                      <span
                        key={info}
                        className="whitespace-nowrap rounded-md bg-lightgray-100 bg-opacity-40 px-2 py-0.5 text-[10px] leading-tight text-lightgray-200"
                      >
                        {info}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          {/* self-end overrides the row's items-start so the flight number and
              status badge sit on the bottom edge, level with the seat boxes
              rather than with the headings above them. */}
          <div className="md:shrink-0 md:ml-auto md:self-end">
            <div className="flex items-center justify-between gap-4 border border-gray-200 bg-white h-[41px] pr-1 pl-4 rounded-lg lg:max-w-[234px] lg:ml-auto">
              <p className="text-[#262626] text-lg font-semibold">
                {flight?.flightNo}
              </p>
              <p
                className={`text-white ${badge.className} rounded px-3 py-1.5 text-sm font-medium capitalize`}
              >
                {badge.text}
              </p>
            </div>
          </div>
        </div>

        {/* Live flight status — subscribes only while this accordion is open
            AND the user enabled status updates. Renders nothing until the
            backend publishes a doc, so the schedule above stays the fallback. */}
        {enabled && <LiveFlightStatus status={status} />}
      </div>
    </div>
  );
};

export default AnsweredFlightDetails;
